import { AuthenticationResult, IPublicClientApplication, SilentRequest } from '@azure/msal-browser';
export interface UserInfo {
    fullName: string;
    isAdmin: boolean;
    isAdminOrMt: boolean;
    isAppOwner: boolean;
    email: string;
    isPm: boolean;
    isSe: boolean;
    isDm: boolean;
    isTerritoryCiso: boolean;
    isTerritoryCio: boolean;
    isTerritoryPm: boolean;
    isMt: boolean;
    canSeeApps: boolean | null;
    hasAnyRole: boolean;
};

export interface DictionaryInfo {
    name: string;
};

export interface DomainsInfo {
    domain: string;
};

export interface UserSearchInfo {
    name?: string | null;
    email?: string | null;
    hint?: string | null;
    isFreeTextValue?: boolean | null;
};

export class MpApiError extends Error {
    operationName: string;
    httpStatusCode: number | null;
    opertationResponse: string | null;
    constructor(message: string, opName: string, statusCode?: number, opResponse?: string) {
        super(message);
        Object.setPrototypeOf(this, MpApiError.prototype);
        this.operationName = opName;
        this.httpStatusCode = statusCode;
        this.opertationResponse = opResponse;
    }
}

export const loadUserInfo = async (instance: IPublicClientApplication): Promise<UserInfo | null> => {
    const tokenResponse = await authenticateOnScope(instance, scopeApi);
    const response = await fetch('/env/getUserInfo', {
        method: 'GET',
        headers: new Headers({
            'Authorization': 'Bearer ' + tokenResponse.accessToken,
            'Content-Type': 'application/json'
        })
    });
    await checkValidResponse(response, "getUserInfo");
    const userInfo: UserInfo = <UserInfo>(await response.json());
    userInfo.canSeeApps = userInfo.isAdminOrMt || userInfo.isAppOwner || userInfo.isSe || userInfo.isDm || userInfo.isTerritoryCio || userInfo.isTerritoryCiso || userInfo.isTerritoryPm;
    return userInfo;
};

export const authenticateOnScope = async (instance: IPublicClientApplication, currentScopes: string[]): Promise<AuthenticationResult> => {
    const activeAccount = instance.getActiveAccount();
    const silentRequest = {
        account: activeAccount,
        scopes: currentScopes
    } as const satisfies SilentRequest;
    return await instance.acquireTokenSilent(silentRequest);
}

export const getTerritories = async (): Promise<DictionaryInfo[]> => {
    const response = await fetch('/app/getTerritories', {
        method: 'GET',
        headers: new Headers({
            'Content-Type': 'application/json'
        })
    });
    await checkValidResponse(response, "getTerritories");
    return await response.json();
}

export const checkValidResponse = async (response: Response, operationName: string) => {
    if (response.status > 199 && response.status < 300) {
        return;
    }
    const error = new MpApiError(response.statusText, operationName, response.status);
    try {
        const details = await response.text();
        error.opertationResponse = details;
        throw error;
    }
    catch {
        throw error;
    }
};

export const scopePowerBi = ["https://analysis.windows.net/powerbi/api/Report.Read.All"];

export const scopeApi = ["email", "api://" + localStorage.getItem("MpWebApiClientId") + "/pwc-Identity-migration-portal-api"];


// Cache domains in memory once loaded
let cachedDomains: string[] = [];
let domainsLoaded = false;

export const getDomains = async (): Promise<DomainsInfo[]> => {
    console.log("Calling /domains/getDomains endpoint...");
    const response = await fetch('/domains/getDomains', { // Changed to lowercase
        method: 'GET',
        headers: new Headers({
            'Content-Type': 'application/json'
        })
    });
    console.log("Response status:", response.status);
    await checkValidResponse(response, "getDomains");
    const result = await response.json();
    return result;
}

// Load domains into cache
export const loadAllowedDomains = async (): Promise<void> => {
    if (!domainsLoaded) {
        try {
            const domainsInfo = await getDomains();
            cachedDomains = domainsInfo.map(d => d.domain).filter(Boolean) as string[];
            domainsLoaded = true;
        } catch (error) {
            console.error('Failed to load allowed domains:', error);
        }
    }
};

// Async validator
export const pwcEmailValidator = async (value: string): Promise<string | null> => {
    // Ensure domains are loaded
    await loadAllowedDomains();
    
    if (!value.includes('@')) {
        return 'Invalid email address';
    }

    const domain = value.split('@')[1].toLowerCase();
    if (!cachedDomains.includes(domain)) {
        return 'Invalid PwC email address';
    }

    return null;
};

// Synchronous validator for forms that can't handle async
export const pwcEmailValidatorSync = (value: string): string | null => {
    if (!value.includes('@')) {
        return 'Invalid email address';
    }

    // If domains aren't loaded yet, assume valid (will be validated server-side)
    if (!domainsLoaded || cachedDomains.length === 0) {
        return null;
    }

    const domain = value.split('@')[1].toLowerCase();
    if (!cachedDomains.includes(domain)) {
        return 'Invalid PwC email address';
    }

    return null;
};

export const emailValidator = value => {
    if (!/^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/.test(value))
        return "Invalid email address";
    return false;
};

export const urnValidator = value => {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value))
        return "Invalid URN";
    return false;
};